import { Amplify } from 'aws-amplify';
import { get, post } from 'aws-amplify/api';
import amplifyConfig from '../amplifyconfiguration.json';

/**
 * Simplified API Service for Euroshop Ticket Map Dashboard
 * Uses AWS Amplify with amplifyconfiguration.json for clean, environment-free integration
 * Connects to euroshopMapGetTickets Lambda function via /getTickets endpoint
 */

// Initialize Amplify with the configuration
Amplify.configure(amplifyConfig);

// Get API configuration from amplifyconfiguration.json
const getApiConfig = () => {
  const apiConfig = amplifyConfig.aws_cloud_logic_custom?.[0];
  if (!apiConfig) {
    throw new Error('No API configuration found in amplifyconfiguration.json');
  }
  return apiConfig;
};

// Country name to ISO code mapping for Lambda response transformation
const COUNTRY_NAME_TO_CODE = {
  'Italy': 'IT',
  'Germany': 'DE',
  'France': 'FR',
  'Spain': 'ES',
  'United Kingdom': 'GB',
  'Netherlands': 'NL',
  'Belgium': 'BE',
  'Austria': 'AT',
  'Switzerland': 'CH',
  'Poland': 'PL',
  'Portugal': 'PT',
  'Czech Republic': 'CZ',
  'Hungary': 'HU',
  'Slovakia': 'SK',
  'Slovenia': 'SI',
  'Croatia': 'HR',
  'Romania': 'RO',
  'Bulgaria': 'BG',
  'Greece': 'GR',
  'Denmark': 'DK',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Finland': 'FI',
  'Ireland': 'IE',
  'Luxembourg': 'LU',
  'Malta': 'MT',
  'Cyprus': 'CY',
  'Estonia': 'EE',
  'Latvia': 'LV',
  'Lithuania': 'LT'
};

// Simple in-memory cache
const cache = new Map();
const CACHE_TIMEOUT = 10 * 60 * 1000; // 10 minutes

// Cache utility functions
const getCacheKey = (endpoint, params = {}) => {
  const paramString = Object.keys(params).length > 0 ? JSON.stringify(params) : '';
  return `${endpoint}${paramString}`;
};

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.timestamp > CACHE_TIMEOUT) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Clear all cached data
export const clearCache = () => {
  cache.clear();
  console.log('[API] Cache cleared');
};

/**
 * Transform Lambda response to map data format expected by the frontend
 */
const transformLambdaResponseToMapData = (lambdaResponse) => {
  try {
    if (!lambdaResponse || !lambdaResponse.by_location) {
      console.warn('[API] Invalid Lambda response for map data transformation');
      return {
        data: [],
        last_updated: new Date().toISOString()
      };
    }

    const transformedData = [];
    
    // Convert by_location object to array format
    Object.entries(lambdaResponse.by_location).forEach(([countryName, countryData]) => {
      const countryCode = COUNTRY_NAME_TO_CODE[countryName];
      
      if (countryCode && countryData && typeof countryData.country_in_progress === 'number') {
        transformedData.push({
          country_code: countryCode,
          country_name: countryName,
          open_count: countryData.country_in_progress // Use country_in_progress from backend
        });
      } else if (!countryCode) {
        console.warn(`[API] Unknown country name in Lambda response: ${countryName}`);
      }
    });

    console.log('[API] Transformed map data:', transformedData);
    return {
      data: transformedData,
      last_updated: new Date().toISOString()
    };
  } catch (error) {
    console.error('[API] Error transforming Lambda response to map data:', error);
    return {
      data: [],
      last_updated: new Date().toISOString()
    };
  }
};

/**
 * Transform Lambda response to KPI data format expected by the frontend
 */
const transformLambdaResponseToKPIData = (lambdaResponse) => {
  try {
    if (!lambdaResponse || !lambdaResponse.summary || !lambdaResponse.by_location) {
      console.warn('[API] Invalid Lambda response for KPI data transformation');
      return {
        total_open: 0,
        avg_resolution_hours: 48.5,
        top_performer: { country: 'Unknown', closure_rate: 0 },
        sa_opened_today: 0,
        total_closed_today: 0,
        last_updated: new Date().toISOString()
      };
    }

    const { summary, by_location } = lambdaResponse;

    // Extract total_open from summary.total_in_progress
    const total_open = summary.total_in_progress || 0;

    // Use reasonable default for avg_resolution_hours since it's not provided by Lambda
    const avg_resolution_hours = 48.5;

    // Calculate top_performer by finding country with lowest country_in_progress_percentage
    let topPerformer = { country: 'Unknown', closure_rate: 0 };
    let lowestPercentage = Infinity;

    Object.entries(by_location).forEach(([countryName, countryData]) => {
      if (countryData && typeof countryData.country_in_progress_percentage === 'number') {
        if (countryData.country_in_progress_percentage < lowestPercentage) {
          lowestPercentage = countryData.country_in_progress_percentage;
          topPerformer = {
            country: countryName,
            closure_rate: Math.round(countryData.country_in_progress_percentage * 10) / 10 // Round to 1 decimal
          };
        }
      }
    });

    // Use summary.total_created_today for sa_opened_today
    const sa_opened_today = summary.total_created_today || 0;

    // Use summary.total_closed_today for total_closed_today
    const total_closed_today = summary.total_closed_today || 0;

    return {
      total_open,
      avg_resolution_hours,
      top_performer: topPerformer,
      sa_opened_today,
      total_closed_today,
      last_updated: new Date().toISOString()
    };
  } catch (error) {
    console.error('[API] Error transforming Lambda response to KPI data:', error);
    return {
      total_open: 0,
      avg_resolution_hours: 48.5,
      top_performer: { country: 'Unknown', closure_rate: 0 },
      sa_opened_today: 0,
      total_closed_today: 0,
      last_updated: new Date().toISOString()
    };
  }
};

/**
 * AWS Amplify API call wrapper
 */
const callAmplifyAPI = async (path, method = 'GET', data = null) => {
  try {
    const apiConfig = getApiConfig();
    console.log(`[Amplify API] ${method} ${path}`, data ? { requestBody: data } : '');
    
    const apiCall = method === 'GET'
      ? get({ apiName: apiConfig.name, path })
      : post({ apiName: apiConfig.name, path, options: { body: data } });
    
    const apiResponse = await apiCall;
    console.log(`[Amplify API Response] ${method} ${path}`, apiResponse);
    
    // Handle the Amplify response structure - need to await the response.response Promise
    const actualResponse = await apiResponse.response;
    console.log(`[Amplify API Actual Response] ${method} ${path}`, actualResponse);
    
    // Parse JSON from the ReadableStream body
    const jsonData = await actualResponse.body.json();
    console.log(`[Amplify API JSON Data] ${method} ${path}`, jsonData);
    
    return jsonData;
  } catch (error) {
    console.error(`[Amplify API Error] ${method} ${path}:`, error);
    if (data) {
      console.error(`[Amplify API Error] Request body:`, data);
    }
    throw error;
  }
};

/**
 * Mock data for fallback scenarios
 */
const getMockMapData = () => ({
  data: [
    { country_code: 'IT', country_name: 'Italy', open_count: 145 },
    { country_code: 'DE', country_name: 'Germany', open_count: 89 },
    { country_code: 'FR', country_name: 'France', open_count: 67 },
    { country_code: 'ES', country_name: 'Spain', open_count: 54 },
    { country_code: 'GB', country_name: 'United Kingdom', open_count: 43 },
    { country_code: 'NL', country_name: 'Netherlands', open_count: 32 },
    { country_code: 'BE', country_name: 'Belgium', open_count: 28 },
    { country_code: 'AT', country_name: 'Austria', open_count: 21 },
    { country_code: 'CH', country_name: 'Switzerland', open_count: 19 },
    { country_code: 'PL', country_name: 'Poland', open_count: 15 }
  ],
  last_updated: new Date().toISOString()
});

const getMockKPIData = () => ({
  total_open: 1234,
  avg_resolution_hours: 48.5,
  top_performer: {
    country: 'Germany',
    closure_rate: 94.2
  },
  sa_opened_today: 45,
  total_closed_today: 78,
  last_updated: new Date().toISOString()
});

// Shared data cache for both map and KPI data
let sharedDataCache = null;
let sharedDataTimestamp = null;

/**
 * Get all ticket data from backend
 * Connects to euroshopMapGetTickets Lambda function via GET /getTickets
 */
const getAllTicketData = async () => {
  const cacheKey = getCacheKey('all-tickets');
  
  // Check cache first
  const cachedData = getCachedData(cacheKey);
  if (cachedData) {
    console.log('[API] Returning cached ticket data');
    return cachedData;
  }
  
  try {
    console.log('[API] Fetching all ticket data from backend...');
    const response = await callAmplifyAPI('/getTickets', 'GET');
    
    console.log('[API] Raw backend response:', response);
    setCachedData(cacheKey, response);
    return response;
  } catch (error) {
    console.error('[API] Failed to fetch ticket data from backend:', error);
    throw error;
  }
};

/**
 * Get map data for world visualization
 * Uses shared backend data and transforms it for map display
 */
export const getMapData = async () => {
  try {
    const backendData = await getAllTicketData();
    
    // Transform Lambda response to expected frontend format
    const transformedData = transformLambdaResponseToMapData(backendData);
    console.log('[API] Map data transformation complete:', transformedData);
    return transformedData;
  } catch (error) {
    console.error('[API] Failed to fetch map data, returning mock data as fallback:', error);
    const mockData = getMockMapData();
    return mockData;
  }
};

/**
 * Get complete ticket data with full API structure for EuropeMap
 * Returns the raw backend data with summary and by_location structure intact
 */
export const getCompleteTicketData = async () => {
  try {
    console.log('[API] Fetching complete ticket data with city information...');
    const backendData = await getAllTicketData();
    
    if (!backendData || !backendData.by_location) {
      console.warn('[API] Invalid backend response for complete ticket data');
      return {
        summary: {
          total_appointments: 0,
          total_in_progress: 0,
          total_created_today: 0,
          overall_in_progress_percentage: 0
        },
        by_location: {},
        last_updated: new Date().toISOString()
      };
    }

    // Add metadata and return complete structure
    const completeData = {
      ...backendData,
      last_updated: new Date().toISOString()
    };
    
    console.log('[API] Complete ticket data loaded:', {
      countries: Object.keys(backendData.by_location).length,
      totalCities: Object.values(backendData.by_location).reduce((total, country) =>
        total + (country.cities ? Object.keys(country.cities).length : 0), 0
      )
    });
    
    return completeData;
  } catch (error) {
    console.error('[API] Failed to fetch complete ticket data:', error);
    throw error;
  }
};

/**
 * Get KPI data
 * Uses shared backend data and transforms it for KPI display
 */
export const getKPIs = async () => {
  try {
    const backendData = await getAllTicketData();
    
    // Transform Lambda response to expected frontend format
    const transformedData = transformLambdaResponseToKPIData(backendData);
    console.log('[API] KPI data transformation complete:', transformedData);
    return transformedData;
  } catch (error) {
    console.error('[API] Failed to fetch KPI data, returning mock data as fallback:', error);
    const mockData = getMockKPIData();
    return mockData;
  }
};

/**
 * Manual refresh trigger - clears cache and fetches fresh data
 */
export const refreshAllData = async () => {
  console.log('[API] Manual refresh triggered - clearing cache');
  clearCache();
  
  try {
    // Since both map and KPI data come from the same backend call,
    // we can fetch once and transform for both
    const backendData = await getAllTicketData();
    
    const mapData = transformLambdaResponseToMapData(backendData);
    const kpiData = transformLambdaResponseToKPIData(backendData);
    
    return {
      mapData,
      kpiData,
      refreshedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('[API] Error during manual refresh:', error);
    throw error;
  }
};

/**
 * Health check endpoint
 * Connects to euroshopMapGetTickets Lambda function via GET /getTickets
 */
export const healthCheck = async () => {
  try {
    const response = await callAmplifyAPI('/getTickets', 'GET');
    return {
      status: 'success',
      data: response,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Get API configuration info for debugging
 */
export const getApiInfo = () => {
  try {
    const apiConfig = getApiConfig();
    return {
      name: apiConfig.name,
      endpoint: apiConfig.endpoint,
      region: apiConfig.region,
      cacheSize: cache.size,
      cacheKeys: Array.from(cache.keys())
    };
  } catch (error) {
    return {
      error: error.message,
      cacheSize: cache.size,
      cacheKeys: Array.from(cache.keys())
    };
  }
};

export default {
  getMapData,
  getKPIs,
  getCompleteTicketData,
  refreshAllData,
  healthCheck,
  clearCache,
  getApiInfo
};